/**
 * Pure mapping between the stored page_selection value and the studio control.
 *
 * The control is a preset dropdown (All pages / First page only / First and
 * last / Custom) plus one custom text field. NO GRAMMAR PARSING happens here or
 * anywhere else in capture-ui — grammar lives server-side in
 * validatePageSelection (capture-app/src/services/pageSelection.ts), and the
 * zone warning comes from the server's preview endpoint. This module only maps
 * strings to dropdown states and back.
 */

export const PAGE_SELECTION_PRESETS = {
  ALL: 'all',
  FIRST_ONLY: 'first_only',
  FIRST_LAST: 'first_last',
  CUSTOM: 'custom',
};

/** The exact strings the presets write. Anything else round-trips as Custom. */
const PRESET_STORED_VALUES = {
  [PAGE_SELECTION_PRESETS.FIRST_ONLY]: 'first',
  [PAGE_SELECTION_PRESETS.FIRST_LAST]: 'first,last',
};

/**
 * stored value -> { preset, customText }
 *
 * Preset detection is an EXACT match on the strings the presets themselves
 * write. A user who typed 'First, Last' into Custom gets Custom back with
 * their own text verbatim — the stored tokens are their typing, not ours.
 */
export function controlFromStored(stored) {
  if (stored == null || String(stored).trim() === '') {
    return { preset: PAGE_SELECTION_PRESETS.ALL, customText: '' };
  }
  const value = String(stored);
  if (value === PRESET_STORED_VALUES[PAGE_SELECTION_PRESETS.FIRST_ONLY]) {
    return { preset: PAGE_SELECTION_PRESETS.FIRST_ONLY, customText: '' };
  }
  if (value === PRESET_STORED_VALUES[PAGE_SELECTION_PRESETS.FIRST_LAST]) {
    return { preset: PAGE_SELECTION_PRESETS.FIRST_LAST, customText: '' };
  }
  return { preset: PAGE_SELECTION_PRESETS.CUSTOM, customText: value };
}

/**
 * control state -> value to store
 *
 * All pages -> null (the column stores NULL for the default, never '').
 * Custom with an empty field -> null too: an empty custom selection IS
 * "all pages", and storing '' would put a second spelling of the default
 * in the column.
 */
export function storedFromControl(preset, customText) {
  switch (preset) {
    case PAGE_SELECTION_PRESETS.FIRST_ONLY:
      return PRESET_STORED_VALUES[PAGE_SELECTION_PRESETS.FIRST_ONLY];
    case PAGE_SELECTION_PRESETS.FIRST_LAST:
      return PRESET_STORED_VALUES[PAGE_SELECTION_PRESETS.FIRST_LAST];
    case PAGE_SELECTION_PRESETS.CUSTOM: {
      const trimmed = (customText ?? '').trim();
      return trimmed === '' ? null : trimmed;
    }
    case PAGE_SELECTION_PRESETS.ALL:
    default:
      return null;
  }
}
